import React, { useCallback, useEffect, useState } from 'react';

import {
  CloseCircleOutlined,
  CustomerServiceOutlined,
  EyeOutlined,
  GlobalOutlined,
  SettingOutlined,
  TruckOutlined,
  WifiOutlined,
} from '@ant-design/icons';
import { CardActions } from '@portal/actions/index';
import Bill from '@portal/assets/svg/cards/bill.svg?react';
import Duplicate from '@portal/assets/svg/cards/duplicate.svg?react';
import Lock from '@portal/assets/svg/cards/lock.svg?react';
import Password from '@portal/assets/svg/cards/password.svg?react';
import Unlock from '@portal/assets/svg/cards/unlock.svg?react';
import AdvancedCard from '@portal/components/AdvancedCard/AdvancedCard';
import AdvancedHeader from '@portal/components/AdvancedHeader/AdvancedHeader';
import AdvancedPanelContentHeader from '@portal/components/AdvancedPanelContentHeader/AdvancedPanelContentHeader';
import CardActionsModal from '@portal/components/CardActionsModal/CardActionsModal';
import CardCodeModal from '@portal/components/CardCodeModal/CardCodeModal';
import CardContact from '@portal/components/CardContact/CardContact';
import CardFirstAccessModal from '@portal/components/CardFirstAccessModal/CardFirstAccessModal';
import CardFlip from '@portal/components/CardFlip/CardFlip';
import CardLimitUpdateModal from '@portal/components/CardLimitUpdateModal/CardLimitUpdateModal';
import CardReceivedBanner from '@portal/components/CardReceivedBanner/CardReceivedBanner';
import CardViewPasswordModal from '@portal/components/CardViewPasswordModal/CardViewPasswordModal';
import ConfirmationModal from '@portal/components/ConfirmationModal/ConfirmationModal';
import GenericModal from '@portal/components/GenericModal/GenericModal';
import ThemedText from '@portal/components/ThemedText/ThemedText';
import { getRouteStackPath } from '@portal/config/routes';
import ErrorsMessages from '@portal/enum/errors';
import RolesType from '@portal/enum/rolesType';
import {
  hasPermissionForFeature,
  TypeModalEnum,
  type ModalState,
} from '@portal/hooks/useCard';
import useReduxState from '@portal/hooks/useReduxState';
import { useSensitiveDataTimer } from '@portal/hooks/useSensitiveDataTimer';
import {
  CardControlEnum,
  CardFirstAccessStatus,
  CardFunction,
  CardStage,
  CardStatusDock,
  CardType,
  ReasonType,
} from '@portal/models/enumerators/cards';
import { encrypt } from '@portal/repositories/securitySdkConfig';
import {
  canConfirmCardReceipt,
  canUnblockByStage,
  handleUnavailableFunctionality,
  isCardBlocked,
  isCardCanceled,
  isFirstAccessPending,
  sanitizePasswordInput,
  shouldRenderFirstAccess,
  validateCardFunctionality,
  validateCardPassword,
  type CardFeature,
} from '@portal/services/cardValidation';
import { translate } from '@portal/services/i18n';
import MessageService from '@portal/services/message';
import { getEnumDescription } from '@portal/utils/enum';
import { hasAccess } from '@portal/utils/hasAccess';
import { pinValidationResolver } from '@portal/utils/pinValidationResolver';
import { Spin, Switch, Tooltip } from 'antd';
import { DateTime } from 'luxon';
import { useDispatch } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';
import './Details.less';

type NotificationType = 'success' | 'error' | 'warning';

const notificationMap = {
  success: MessageService.success,
  error: MessageService.error,
  warning: MessageService.warn,
};

const ERROR_FALLBACKS = {
  BLOCK: 'PAGES.CARDS.MY_CARDS.DETAILS.BLOCK_CARD.BLOCK_ERROR',
  UNBLOCK: 'PAGES.CARDS.MY_CARDS.DETAILS.UNBLOCK_CARD.UNBLOCK_ERROR',
  VIEW_PASSWORD: 'PAGES.CARDS.MY_CARDS.DETAILS.VIEW_PASSWORD.VIEW_ERROR',
  CHANGE_PASSWORD: 'PAGES.CARDS.MY_CARDS.DETAILS.CHANGE_PASSWORD.CHANGE_ERROR',
  CANCEL: 'PAGES.CARDS.MY_CARDS.DETAILS.CANCEL_CARD.CANCEL_ERROR',
  DUPLICATE: 'PAGES.CARDS.MY_CARDS.DETAILS.DUPLICATE_CARD.DUPLICATE_ERROR',
} as const;

const Details = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const params: { id: string } = useParams();

  const {
    user: { roles },
    theme: { currentTheme },
    cards: { cardDetails, error: cardError },
  } = useReduxState();

  const [myPassword, setMyPassword] = useState<string | null>(null);
  const [realCardData, setRealCardData] = useState<bank.Card | null>(null);
  const isBlocked = isCardBlocked(cardDetails?.status as CardStatusDock);

  const { start: startRealCardDataTimer } = useSensitiveDataTimer(
    useCallback(() => {
      setRealCardData(null);
      setIsFlipped(false);
    }, [])
  );

  const { start: startCurrentPasswordVisibilityTimer } = useSensitiveDataTimer(
    useCallback(() => {
      setShowCurrentPassword(false);
    }, [])
  );

  const { start: startViewPasswordVisibilityTimer } = useSensitiveDataTimer(
    useCallback(() => {
      setShowViewPasswordValue(false);
    }, [])
  );

  const [isUpdateLimitModalVisible, setIsUpdateLimitModalVisible] =
    useState(false);
  const [password, setPassword] = useState('');
  const [modalPin, setModalPin] = useState(false);
  const [modalTypeActions, setModalTypeActions] = useState<ModalState>({
    type: TypeModalEnum.UNBLOCK,
    visible: false,
  });
  const [description, setDescription] = useState(
    ReasonType.DISSATISFIED_WITH_THE_CARD
  );
  const [isFlipped, setIsFlipped] = useState(false);
  const [isRevealing, setIsRevealing] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [modalFirstAccess, setModalFirstAccess] = useState(false);
  const [cardCode, setCardCode] = useState('');
  const [modalCardCode, setModalCardCode] = useState(false);
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showViewPasswordValue, setShowViewPasswordValue] = useState(false);
  const [cardControlOptions, setCardControlOptions] = useState({
    feature: CardControlEnum.CONTACTLESS,
    value: false,
  });
  const [passwordsKey, setPasswordsKey] = useState({
    pin: '',
    currentPassword: '',
    newPassword: '',
  });
  const [isCardContactModalVisible, setIsCardContactModalVisible] =
    useState(false);
  const [isConfirmingReceipt, setIsConfirmingReceipt] = useState(false);
  const [confirmAction, setConfirmAction] = useState<CardFeature | null>(null);
  const [isUnlockedByIncorrectPassword, setIsUnlockedByIncorrectPassword] =
    useState(false);
  const [isCardLoading, setIsCardLoading] = useState(true);
  const [firstAccessPin, setFirstAccessPin] = useState('');

  useEffect(() => {
    setIsCardLoading(true);
    setIsFlipped(false);
    setMyPassword(null);
    setRealCardData(null);
    dispatch(CardActions.clearCardDetails());

    if (params.id) {
      dispatch(CardActions.getCardDetails(params.id));
    }

    return () => {
      setMyPassword(null);
      setRealCardData(null);
      dispatch(CardActions.clearCardDetails());
    };
  }, [dispatch, params.id]);

  useEffect(() => {
    if (cardDetails !== null || cardError) {
      setIsCardLoading(false);
    }
  }, [cardDetails, cardError]);

  const closePasswordModal = useCallback(() => {
    setShowPassword(false);
    setShowViewPasswordValue(false);
    setMyPassword(null);
  }, []);

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.hidden) {
        closePasswordModal();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [closePasswordModal]);

  useEffect(() => {
    const unListen = history.listen(() => {
      closePasswordModal();
    });

    return () => {
      unListen();
    };
  }, [history, closePasswordModal]);

  const handleChangeCardLimit = (newLimit: number) => {
    if (newLimit <= 0) {
      return;
    }

    if (!cardDetails?.id) {
      return;
    }

    setIsUpdateLimitModalVisible(false);
  };

  const renderFirstAccess = () => shouldRenderFirstAccess(cardDetails);

  const validateCardFunctionalityLocal = (feature: CardFeature): boolean => {
    const validation = validateCardFunctionality(feature, cardDetails);

    if (!validation.isValid && validation.messageKey) {
      if (validation.reason === 'BLOCKED_OR_CANCELED') {
        const actionText = handleUnavailableFunctionality(feature);
        const message = validation.messageKey?.replace(
          '{{action}}',
          actionText
        );
        openNotification(message, 'warning');
      } else {
        openNotification(validation.messageKey, 'warning');
      }
      return false;
    }

    return validation.isValid;
  };

  const handleFeatureClick = (feature: CardFeature) => {
    if (!cardDetails?.id || !feature) return;
    const isFirstAccess = renderFirstAccess();
    const isUnblock = feature === 'unblock';
    const hasPermission = hasPermissionForFeature(feature, roles);

    if (!hasPermission) {
      openNotification('PAGES.CARDS.MY_CARDS.DETAILS.NO_PERMISSION', 'error');

      return;
    }

    if (!validateCardFunctionalityLocal(feature)) {
      return;
    }

    if (isFirstAccess && isUnblock) {
      setModalTypeActions({ type: TypeModalEnum.UNBLOCK, visible: false });
      setModalPin(true);
      return;
    }

    const featuresRequiringConfirmation: CardFeature[] = [
      'change_password',
      'cancel',
      'duplicate',
    ];

    if (featuresRequiringConfirmation.includes(feature)) {
      setConfirmAction(feature);
      return;
    }

    const featureToModalMap: Partial<Record<CardFeature, TypeModalEnum>> = {
      block: TypeModalEnum.BLOCK,
      unblock: TypeModalEnum.UNBLOCK,
      view_password: TypeModalEnum.VIEW_PASSWORD,
    };

    const modalType = featureToModalMap[feature];

    if (!modalType) return;

    setModalTypeActions({
      type: modalType,
      visible: true,
    });
  };

  const handleConfirmAction = () => {
    if (!confirmAction) return;

    const featureToModalMap: Partial<Record<CardFeature, TypeModalEnum>> = {
      cancel: TypeModalEnum.CANCEL,
      change_password: TypeModalEnum.CHANGE_PASSWORD,
      duplicate: TypeModalEnum.DUPLICATE,
    };

    const modalType = featureToModalMap[confirmAction];
    setConfirmAction(null);

    if (!modalType) return;

    setPassword('');
    setDescription(ReasonType.DISSATISFIED_WITH_THE_CARD);

    setModalTypeActions({ type: modalType, visible: true });
  };

  const handleNavigateToExtract = () => {
    history.push(getRouteStackPath('EXTRACT', 'EXTRACT_PAGE'));
  };

  const handleNavigateToTracking = () => {
    const hasTrackingAccess = hasAccess(roles, RolesType.CARD_TRACKING_READ);

    if (!hasTrackingAccess) {
      openNotification(
        'PAGES.CARDS.MY_CARDS.DETAILS.TRACKING.NO_PERMISSION',
        'error'
      );

      return;
    }

    history.push(
      getRouteStackPath('CARDS', 'TRACKING_CARD', { id: cardDetails?.id ?? '' })
    );
  };

  const handleConfirmCardReceived = (cardId: string) => {
    setIsConfirmingReceipt(true);

    dispatch(
      CardActions.confirmCardReceived(cardId, (err) => {
        setIsConfirmingReceipt(false);
        if (err) {
          openNotification(
            'PAGES.CARDS.MY_CARDS.DETAILS.CARD_RECEIVED_BANNER.ERROR_DESCRIPTION',
            'error'
          );

          return;
        }

        openNotification(
          'PAGES.CARDS.MY_CARDS.DETAILS.CARD_RECEIVED_BANNER.SUCCESS_DESCRIPTION',
          'success'
        );
        dispatch(CardActions.getCardDetails(params.id));
      })
    );
  };

  const handleViewTrackingFromBanner = () => {
    handleNavigateToTracking();
  };

  const renderCardActions = () => {
    const actions = [
      {
        visible: true,
        name: 'change_password',
        title: translate('PAGES.CARDS.FUNCTIONS.CHANGE_PASSWORD'),
        icon: <Password />,
        role: RolesType.CARD_PIN_RESET,
        onClick: () => handleFeatureClick('change_password'),
      },
      {
        visible: true,
        name: 'view_data',
        title: translate('PAGES.CARDS.MY_CARDS.DETAILS.VIEW_DATA.TITLE'),
        icon: <EyeOutlined />,
        onClick: () => {
          if (isFlipped) handleToggleFlip();
          else handleViewData();
        },
      },
      {
        visible: true,
        name: 'bill',
        title: translate('PAGES.CARDS.FUNCTIONS.EXTRACT'),
        icon: <Bill />,
        onClick: () => handleNavigateToExtract(),
      },
      {
        visible: true,
        name: 'password',
        title: translate('PAGES.CARDS.FUNCTIONS.VIEW_PASSWORD'),
        icon: <Lock />,
        onClick: () => handleFeatureClick('view_password'),
      },
      {
        visible: cardDetails?.type === CardType.PHYSICAL,
        name: 'duplicate',
        title: translate('PAGES.CARDS.FUNCTIONS.REQUEST_DUPLICATE'),
        icon: <Duplicate />,
        role: RolesType.CARD_DUPLICATE_REQUEST,
        onClick: () => handleFeatureClick('duplicate'),
      },
      {
        visible: isFirstAccessPending(
          cardDetails?.type,
          cardDetails?.firstAccessStatus
        ),
        name: 'tracking',
        title: translate('PAGES.CARDS.FUNCTIONS.TRACKING'),
        icon: <TruckOutlined />,
        onClick: () => handleNavigateToTracking(),
      },
      {
        visible: true,
        name: 'cancel',
        title: translate('PAGES.CARDS.FUNCTIONS.CANCEL'),
        role: RolesType.CARD_CANCEL_REQUEST,
        icon: <CloseCircleOutlined />,
        onClick: () => handleFeatureClick('cancel'),
      },
    ];

    return (
      <div className="card-details__actions-grid">
        {actions
          .filter((action) => action.visible)
          .map((action) => (
            <button
              className="card-details__action-btn"
              key={action.name}
              onClick={() => action.onClick?.()}
              disabled={isBlocked ? action.name !== 'tracking' : undefined}
              style={{
                opacity: isBlocked && action.name !== 'tracking' ? 0.4 : 1,
                cursor:
                  isBlocked && action.name !== 'tracking'
                    ? 'not-allowed'
                    : 'pointer',
              }}
            >
              <div
                className="card-details__action-btn__icon"
                style={{ background: currentTheme.background }}
              >
                {action.icon}
              </div>
              <ThemedText variant="caption">{action.title}</ThemedText>
            </button>
          ))}
      </div>
    );
  };

  const formatExpirationDate = (dateString: string) => {
    const date = DateTime.fromISO(dateString, { zone: 'utc' });
    return date.isValid ? date.toFormat('MM/yy') : '--/--';
  };

  const handleToggleFlip = () => {
    setIsFlipped((prev) => !prev);
  };

  useEffect(() => {
    const resetSensitiveCardData = () => {
      setIsFlipped(false);
      setRealCardData(null);
    };
    const handleVisibilityChange = () => {
      if (document.hidden) resetSensitiveCardData();
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('blur', resetSensitiveCardData);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('blur', resetSensitiveCardData);
    };
  }, []);

  const handleViewData = () => {
    if (isRevealing || !cardDetails?.id) return;
    setModalTypeActions({
      type: TypeModalEnum.VIEW_CARD_DATA,
      visible: false,
    });
    setModalPin(true);
  };

  const renderCardDetails = () => {
    const status = cardDetails?.status as CardStatusDock;
    const isCanceled = isCardCanceled(status);

    const tagVariant = isCanceled
      ? 'canceled'
      : isBlocked
      ? 'blocked'
      : 'active';

    return (
      <div className="card-details__card-wrapper">
        <CardFlip
          isFlipped={isFlipped}
          isLoading={isRevealing}
          background={currentTheme.background}
          cardNumber={realCardData?.cardNumber}
          expirationDate={formatExpirationDate(
            realCardData?.expirationDate || ''
          )}
          holderName={realCardData?.tag || realCardData?.cardHolderName}
          cvv={realCardData?.cvv2}
          type={cardDetails?.type ?? CardType.VIRTUAL}
          status={cardDetails?.status as CardStatusDock}
        />
        <div className="card-details__status-tag-wrapper">
          <span
            className={`card-details__status-tag card-details__status-tag--${tagVariant}`}
          >
            {getEnumDescription(cardDetails?.status ?? '', 'CARD_STATUS_DOCK')}
          </span>
        </div>
      </div>
    );
  };

  const handleCloseModal = (resetState = true) => {
    if (resetState) {
      setPassword('');
      setIsUnlockedByIncorrectPassword(false);
      setDescription(ReasonType.DISSATISFIED_WITH_THE_CARD);
      setPasswordsKey({ pin: '', currentPassword: '', newPassword: '' });
      setShowCurrentPassword(false);
    }

    setModalTypeActions({
      ...modalTypeActions,
      visible: false,
    });
  };

  const openNotification = (description: string, type: NotificationType) => {
    notificationMap[type]?.(description);
  };

  const handleSuccess = (successMessage: string, isPassword?: boolean) => {
    openNotification(successMessage, 'success');

    if (isPassword) {
      setShowPassword(true);

      return;
    }

    dispatch(CardActions.getCardDetails(params.id));
  };

  const handleError = (
    translationKey: string,
    isPinError = false,
    rawErrorMessage?: string
  ) => {
    if (
      rawErrorMessage === ErrorsMessages.YOU_CANNOT_PERFORM_THIS ||
      (rawErrorMessage === ErrorsMessages.CARD_NOT_FOUND &&
        cardDetails?.firstAccessStatus !== CardFirstAccessStatus.PENDING)
    ) {
      openNotification(rawErrorMessage.toUpperCase(), 'error');
      history.push(getRouteStackPath('CARDS', 'MY_CARDS_PAGE'));
      return;
    }

    if (isPinError) {
      setModalPin(true);
    }

    openNotification(translationKey, 'error');
  };

  const buildCardControlConfig = (type: CardControlEnum) => {
    const statusMap = {
      [CardControlEnum.CONTACTLESS]: cardDetails?.contactlessEnabled ?? false,
      [CardControlEnum.INTERNATIONAL]:
        cardDetails?.internationalEnabled ?? false,
    } as const;

    const isDisabling = statusMap[type];

    return {
      permissions: {
        allowsContactlessShopping:
          type === CardControlEnum.CONTACTLESS
            ? !isDisabling
            : statusMap[CardControlEnum.CONTACTLESS],
        allowsInternationalShopping:
          type === CardControlEnum.INTERNATIONAL
            ? !isDisabling
            : statusMap[CardControlEnum.INTERNATIONAL],
      },
      successMessage: isDisabling
        ? `PAGES.CARDS.MY_CARDS.CARD_CONTROL.MESSAGES.DISABLED.${type.toUpperCase()}_SUCCESS`
        : `PAGES.CARDS.MY_CARDS.CARD_CONTROL.MESSAGES.ADDED.${type.toUpperCase()}_SUCCESS`,
      errorMessage: isDisabling
        ? `PAGES.CARDS.MY_CARDS.CARD_CONTROL.MESSAGES.DISABLED.${type.toUpperCase()}_ERROR`
        : `PAGES.CARDS.MY_CARDS.CARD_CONTROL.MESSAGES.ADDED.${type.toUpperCase()}_ERROR`,
    };
  };

  const handleSubmit = async (pinValue: string) => {
    setModalPin(false);
    if (!cardDetails) return;

    const { id: cardId = '' } = cardDetails;
    const { type } = modalTypeActions;

    const encryptedPin = encrypt(pinValue);
    const isUnblock = type === TypeModalEnum.UNBLOCK;
    const isFirstAccess = renderFirstAccess();

    const handleActionSuccess =
      (message: string, closeModal = false) =>
      () =>
        handleSuccess(message, closeModal);

    if (isFirstAccess && isUnblock) {
      dispatch(
        CardActions.viewPasswordV3(cardId, encryptedPin, (err, password) => {
          if (err) {
            const { message, isPinError } = pinValidationResolver({
              err,
              fallbackKey: ERROR_FALLBACKS.VIEW_PASSWORD,
            });

            handleError(message, isPinError, err.message);
            return;
          }

          setMyPassword(password ?? null);
          setFirstAccessPin(encryptedPin);
          setCardCode('');
          setModalCardCode(true);
        })
      );
      return;
    }

    const actions: Record<TypeModalEnum, (() => void) | undefined> = {
      [TypeModalEnum.BLOCK]: () =>
        dispatch(
          CardActions.blockCardV3(
            cardId,
            CardStatusDock.BLOCKED,
            encryptedPin,
            (err) => {
              if (!err) {
                handleActionSuccess(
                  'PAGES.CARDS.MY_CARDS.DETAILS.BLOCK_CARD.BLOCK_SUCCESS'
                )();

                return;
              }

              const { message, isPinError } = pinValidationResolver({
                err,
                fallbackKey:
                  'PAGES.CARDS.MY_CARDS.DETAILS.BLOCK_CARD.BLOCK_ERROR',
              });

              handleError(message, isPinError, err.message);
            }
          )
        ),
      [TypeModalEnum.UNBLOCK]: () =>
        dispatch(
          CardActions.unblockCardV3(cardId, encryptedPin, (err) => {
            if (!err) {
              handleActionSuccess(
                'PAGES.CARDS.MY_CARDS.DETAILS.UNBLOCK_CARD.UNBLOCK_SUCCESS'
              )();

              return;
            }

            const { message, isPinError } = pinValidationResolver({
              err,
              fallbackKey:
                'PAGES.CARDS.MY_CARDS.DETAILS.UNBLOCK_CARD.UNBLOCK_ERROR',
            });

            handleError(message, isPinError, err.message);
          })
        ),
      [TypeModalEnum.CANCEL]: () =>
        dispatch(
          CardActions.cancelCardV3(cardId, description, encryptedPin, (err) => {
            if (err) {
              const { message, isPinError } = pinValidationResolver({
                err,
                fallbackKey: ERROR_FALLBACKS.CANCEL,
              });

              handleError(message, isPinError, err.message);
              return;
            }

            handleSuccess(
              'PAGES.CARDS.MY_CARDS.DETAILS.CANCEL_CARD.CANCEL_SUCCESS'
            );

            history.push(getRouteStackPath('CARDS', 'MY_CARDS_PAGE'));
          })
        ),
      [TypeModalEnum.CHANGE_PASSWORD]: () =>
        dispatch(
          CardActions.changePasswordV3(
            cardId,
            password,
            isUnlockedByIncorrectPassword,
            encryptedPin,
            (err) => {
              if (err) {
                const { message, isPinError } = pinValidationResolver({
                  err,
                  fallbackKey: ERROR_FALLBACKS.CHANGE_PASSWORD,
                });

                handleError(message, isPinError, err.message);
                return;
              }

              handleSuccess(
                'PAGES.CARDS.MY_CARDS.DETAILS.CHANGE_PASSWORD.CHANGE_SUCCESS'
              );
              setPassword('');
              setIsUnlockedByIncorrectPassword(false);
            }
          )
        ),
      [TypeModalEnum.VIEW_PASSWORD]: () =>
        dispatch(
          CardActions.viewPasswordV3(cardId, encryptedPin, (err, password) => {
            if (err) {
              const { message, isPinError } = pinValidationResolver({
                err,
                fallbackKey: ERROR_FALLBACKS.VIEW_PASSWORD,
              });

              handleError(message, isPinError, err.message);
              return;
            }

            setMyPassword(password ?? null);
            handleSuccess(
              'PAGES.CARDS.MY_CARDS.DETAILS.VIEW_PASSWORD.VIEW_SUCCESS',
              true
            );
          })
        ),
      [TypeModalEnum.CARD_CONTROL]: () =>
        dispatch(
          CardActions.cardControlV3(
            cardId,
            encryptedPin,
            buildCardControlConfig(
              cardControlOptions?.feature as CardControlEnum
            ).permissions,
            (err) => {
              if (err) {
                const { message, isPinError } = pinValidationResolver({
                  err,
                  fallbackKey: buildCardControlConfig(
                    cardControlOptions?.feature as CardControlEnum
                  ).errorMessage,
                });

                handleError(message, isPinError, err.message);
                return;
              }

              handleSuccess(
                buildCardControlConfig(
                  cardControlOptions?.feature as CardControlEnum
                ).successMessage
              );
            }
          )
        ),
      [TypeModalEnum.VIEW_CARD_DATA]: () => {
        setIsRevealing(true);
        dispatch(
          CardActions.viewCardDataV3(cardId, encryptedPin, (err, data) => {
            setIsRevealing(false);

            if (err) {
              const { message, isPinError } = pinValidationResolver({
                err,
                fallbackKey: 'PAGES.CARDS.MY_CARDS.DETAILS.VIEW_DATA.ERROR',
              });

              handleError(message, isPinError, err.message);
              return;
            }

            setRealCardData(data ?? null);
            startRealCardDataTimer();
            setIsFlipped(true);
          })
        );
      },
      [TypeModalEnum.DUPLICATE]: () =>
        dispatch(
          CardActions.requestDuplicateCard(
            cardId,
            description,
            encryptedPin,
            (err) => {
              if (err) {
                const { message, isPinError } = pinValidationResolver({
                  err,
                  fallbackKey: ERROR_FALLBACKS.DUPLICATE,
                });

                handleError(message, isPinError, err.message);
                return;
              }

              handleSuccess(
                'PAGES.CARDS.MY_CARDS.DETAILS.DUPLICATE_CARD.DUPLICATE_SUCCESS'
              );
              history.push(getRouteStackPath('CARDS', 'MY_CARDS_PAGE'));
            }
          )
        ),
    };

    actions[type]?.();

    setModalTypeActions((prev) => ({
      ...prev,
      visible: false,
    }));
  };

  const getModalTitleAndDescription = () => {
    const modalConfig: Record<
      TypeModalEnum,
      { title: string; description: string }
    > = {
      [TypeModalEnum.BLOCK]: {
        title: translate('PAGES.CARDS.MY_CARDS.DETAILS.BLOCK_CARD.TITLE'),
        description: translate(
          'PAGES.CARDS.MY_CARDS.DETAILS.BLOCK_CARD.BLOCK_TEMPORARY.TEXT'
        ),
      },
      [TypeModalEnum.CANCEL]: {
        title: translate('PAGES.CARDS.MY_CARDS.DETAILS.CANCEL_CARD.TITLE'),
        description: translate('PAGES.CARDS.MY_CARDS.DETAILS.CANCEL_CARD.TEXT'),
      },
      [TypeModalEnum.UNBLOCK]: {
        title: translate('PAGES.CARDS.MY_CARDS.DETAILS.UNBLOCK_CARD.TITLE'),
        description: renderFirstAccess()
          ? translate(
              'PAGES.CARDS.MY_CARDS.DETAILS.UNBLOCK_CARD.FIRST_ACCESS.DESCRIPTION'
            )
          : translate('PAGES.CARDS.MY_CARDS.DETAILS.UNBLOCK_CARD.TEXT'),
      },
      [TypeModalEnum.CHANGE_PASSWORD]: {
        title: translate('PAGES.CARDS.CHANGE_PASSWORD_MODAL.TITLE'),
        description: translate(
          'PAGES.CARDS.MY_CARDS.DETAILS.CHANGE_PASSWORD.TEXT'
        ),
      },
      [TypeModalEnum.VIEW_PASSWORD]: {
        title: translate('PAGES.CARDS.MY_CARDS.DETAILS.VIEW_PASSWORD.TITLE'),
        description: translate(
          'PAGES.CARDS.MY_CARDS.DETAILS.VIEW_PASSWORD.TEXT'
        ),
      },
      [TypeModalEnum.VIEW_CARD_DATA]: {
        title: translate('PAGES.CARDS.MY_CARDS.DETAILS.VIEW_DATA.TITLE'),
        description: translate('PAGES.CARDS.MY_CARDS.DETAILS.VIEW_DATA.TEXT'),
      },
      [TypeModalEnum.CARD_CONTROL]: {
        title: translate('PAGES.CARDS.MY_CARDS.CARD_CONTROL.TITLE'),
        description: translate('PAGES.CARDS.MY_CARDS.CARD_CONTROL.DESCRIPTION'),
      },
      [TypeModalEnum.DUPLICATE]: {
        title: translate('PAGES.CARDS.MY_CARDS.DETAILS.DUPLICATE_CARD.TITLE'),
        description: translate(
          'PAGES.CARDS.MY_CARDS.DETAILS.DUPLICATE_CARD.DESCRIPTION'
        ),
      },
    };

    return (
      modalConfig[modalTypeActions.type] ?? {
        title: '',
        description: '',
      }
    );
  };

  const handlePinModal = () => {
    handleCloseModal(false);

    setModalPin(true);
  };

  const unblockByIncorrectPassword = () => {
    if (
      cardDetails?.status === CardStatusDock.LOCKED_INCORRECT_PASSWORD &&
      modalTypeActions.type === TypeModalEnum.UNBLOCK
    ) {
      return true;
    }

    return false;
  };

  const handleUnlockedByIncorrectPassword = () => {
    setPassword('');
    setIsUnlockedByIncorrectPassword(true);

    setModalTypeActions({
      type: TypeModalEnum.CHANGE_PASSWORD,
      visible: true,
    });
  };

  const renderCancelOptions = () => {
    const excludeTypes = [
      ReasonType.CANCELED_AT_ADM_REQUEST,
      ReasonType.CANCELED_AT_CUSTOMER_REQUEST,
    ];

    const virtualAllowedReasons = [
      ReasonType.DISSATISFIED_WITH_THE_CARD,
      ReasonType.CANCELED_AT_CUSTOMER_REQUEST,
      ReasonType.CANCELED_AT_ADM_REQUEST,
      ReasonType.INFREQUENT_USE,
      ReasonType.CUSTOMER_SERVICE_ISSUES,
      ReasonType.SECURITY_OR_FRAUD_ISSUES,
    ];

    return Object.keys(ReasonType)
      .filter((key) => isNaN(Number(key)))
      .filter((key) => {
        const reasonType = ReasonType[key as keyof typeof ReasonType];
        const isExcluded = excludeTypes.includes(reasonType);
        const isVirtual = cardDetails?.type === CardType.VIRTUAL;

        if (isVirtual && !virtualAllowedReasons.includes(reasonType)) {
          return false;
        }

        return !isExcluded;
      })
      .map((key) => {
        const value = ReasonType[key as keyof typeof ReasonType];

        return {
          name: getEnumDescription(value, 'CANCELED_CARD_REASON'),
          value,
        };
      });
  };

  const renderBlockOrUnblock = () => {
    if (!cardDetails) return null;

    const { status, cardStage, type, isReadyToUnlock } = cardDetails;

    const isCanceled = isCardCanceled(status as CardStatusDock);
    const canUnblock = canUnblockByStage(cardStage, type, isReadyToUnlock);

    const requiredRole = isBlocked
      ? RolesType.CARD_UNBLOCK
      : RolesType.CARD_BLOCK;

    const canManage = hasAccess(roles, requiredRole);
    const isDisabled = !canManage || isCanceled;

    const config = {
      title: isBlocked
        ? translate('PAGES.CARDS.FUNCTIONS.UNBLOCK')
        : translate('PAGES.CARDS.FUNCTIONS.BLOCK'),
      tooltip: isBlocked
        ? translate('PAGES.CARDS.MY_CARDS.DETAILS.UNBLOCK_CARD.NO_PERMISSION')
        : translate('PAGES.CARDS.MY_CARDS.DETAILS.BLOCK_CARD.NO_PERMISSION'),
      Icon: isBlocked ? Lock : Unlock,
      action: isBlocked ? 'unblock' : 'block',
    };

    const handleToggle = () => {
      if (renderFirstAccess() && !canUnblock) {
        openNotification(
          'PAGES.CARDS.MY_CARDS.DETAILS.UNAVAILABLE_STAGE',
          'warning'
        );
        return;
      }

      handleFeatureClick(config.action as CardFeature);
    };

    const switchComponent = (
      <Switch
        checked={isBlocked}
        onChange={handleToggle}
        disabled={isDisabled}
        className={
          isBlocked
            ? 'card-details__switch--blocked'
            : 'card-details__switch--active'
        }
      />
    );

    return (
      <div className="card-details__block-toggle">
        <div className="card-details__block-toggle__info">
          <div
            className={`card-details__block-toggle__icon ${
              isBlocked
                ? 'card-details__block-toggle__icon--blocked'
                : 'card-details__block-toggle__icon--active'
            }`}
          >
            <config.Icon />
          </div>
          <div>
            <ThemedText className="card-details__block-toggle__title">
              {isBlocked
                ? translate('PAGES.CARDS.MY_CARDS.DETAILS.CARD_BLOCKED')
                : translate('PAGES.CARDS.MY_CARDS.DETAILS.CARD_UNBLOCKED')}
            </ThemedText>
            <ThemedText className="card-details__block-toggle__description">
              {isBlocked
                ? translate('PAGES.CARDS.MY_CARDS.DETAILS.CLICK_TO_UNBLOCK')
                : translate('PAGES.CARDS.MY_CARDS.DETAILS.CLICK_TO_BLOCK')}
            </ThemedText>
          </div>
        </div>
        {!canManage && !isCanceled ? (
          <Tooltip title={config.tooltip}>
            <span>{switchComponent}</span>
          </Tooltip>
        ) : (
          switchComponent
        )}
      </div>
    );
  };

  const handleChangePassword = (value: string) => {
    const sanitized = sanitizePasswordInput(value);

    if (sanitized.length === 4) {
      const validation = validateCardPassword(sanitized);

      if (!validation.valid && validation.error) {
        openNotification(validation.error, 'warning');
        return;
      }
    }

    setPassword(sanitized);
  };

  const renderCardReceivedBanner = () => {
    if (!cardDetails || !canConfirmCardReceipt(cardDetails)) {
      return null;
    }

    return (
      <CardReceivedBanner
        card={cardDetails}
        onConfirmReceived={handleConfirmCardReceived}
        onViewTracking={handleViewTrackingFromBanner}
        loading={isConfirmingReceipt}
      />
    );
  };

  const renderMessageCard = () => {
    const card = cardDetails;

    if (!card) return null;

    const {
      type,
      status,
      cardStage = 0,
      firstAccessStatus,
      isReadyToUnlock,
    } = card;

    const isPhysicalBlocked =
      type === CardType.PHYSICAL && status === CardStatusDock.BLOCKED;

    if (!isPhysicalBlocked) return null;

    const isDelivered = cardStage >= CardStage.FORWARDED;
    const isCanceled = (status as CardStatusDock) === CardStatusDock.CANCELED;

    const isFirstAccessPending = [
      CardFirstAccessStatus.PENDING,
      null,
      undefined,
    ].includes(firstAccessStatus);

    const isFirstAccessDone =
      firstAccessStatus === CardFirstAccessStatus.COMPLETED;

    if (isDelivered && isFirstAccessDone) return null;

    const translationKey = isCanceled
      ? 'PAGES.CARDS.MY_CARDS.DETAILS.CANCEL_MESSAGE'
      : isDelivered && isFirstAccessPending && !isReadyToUnlock
      ? 'PAGES.CARDS.MY_CARDS.DETAILS.CARD_AWAITING_CONFIRMATION'
      : isDelivered && isFirstAccessPending
      ? 'PAGES.CARDS.MY_CARDS.DETAILS.CARD_DELIVERED'
      : 'PAGES.CARDS.MY_CARDS.DETAILS.CARD_NOT_AVAILABLE';

    return (
      <div className="card-details__canceled-message">
        <ThemedText variant="body">{translate(translationKey)}</ThemedText>
      </div>
    );
  };

  const handleCardControlChange =
    (feature: CardControlEnum) => (value: boolean) => {
      setCardControlOptions({
        feature,
        value,
      });
      setModalTypeActions({
        type: TypeModalEnum.CARD_CONTROL,
        visible: true,
      });
    };

  const renderCardControl = () => {
    const disabledSwitch = [
      CardStatusDock.CANCELED,
      CardStatusDock.BLOCKED,
      CardStatusDock.LOCKED_INCORRECT_PASSWORD,
    ].includes(cardDetails?.status as CardStatusDock);
    const canManage = hasAccess(roles, RolesType.CARD_MANAGE);

    const options = [
      {
        feature: CardControlEnum.CONTACTLESS,
        label: translate('PAGES.CARDS.MY_CARDS.DETAILS.CONTACTLESS'),
        icon: <WifiOutlined />,
        value: cardDetails?.contactlessEnabled || false,
      },
      {
        feature: CardControlEnum.INTERNATIONAL,
        label: translate(
          'PAGES.CARDS.MY_CARDS.DETAILS.INTERNATIONAL_PURCHASES'
        ),
        icon: <GlobalOutlined />,
        value: cardDetails?.internationalEnabled || false,
      },
    ];

    return (
      <div
        className="card-details__controls"
        style={{ opacity: isBlocked ? 0.4 : 1 }}
      >
        <div className="card-details__controls__row">
          <SettingOutlined />
          <ThemedText>
            {translate('PAGES.CARDS.MY_CARDS.CARD_CONTROL.TITLE')}
          </ThemedText>
        </div>
        <div className="card-details__controls__grid">
          {options.map((option) => (
            <div className="card-details__controls__card" key={option.label}>
              <div className="card-details__controls__card__left">
                <div
                  className="card-details__controls__card__icon"
                  style={{ background: currentTheme.background }}
                >
                  {option.icon}
                </div>
                <div className="card-details__controls__card__text">
                  <ThemedText className="card-details__controls__card__title">
                    {option.label}
                  </ThemedText>
                  <ThemedText className="card-details__controls__card__description">
                    {option.value
                      ? translate('PAGES.CARDS.MY_CARDS.DETAILS.ACTIVE')
                      : translate('PAGES.CARDS.MY_CARDS.DETAILS.INACTIVE')}
                  </ThemedText>
                </div>
              </div>
              <Switch
                checked={option.value}
                onChange={handleCardControlChange(option.feature)}
                disabled={!canManage || disabledSwitch}
              />
            </div>
          ))}
        </div>
      </div>
    );
  };

  const handleCloseFirstAccess = () => {
    setPasswordsKey({ pin: '', currentPassword: '', newPassword: '' });
    setShowCurrentPassword(false);
    setMyPassword(null);
    setModalFirstAccess(false);
  };

  const handleSubmitFirstAccess = () => {
    const newPasswordValidation = validateCardPassword(
      passwordsKey.newPassword
    );

    if (!newPasswordValidation.valid && newPasswordValidation.error) {
      openNotification(newPasswordValidation.error, 'warning');
      return;
    }

    if (passwordsKey.currentPassword !== myPassword) {
      openNotification(
        'PAGES.CARDS.MY_CARDS.DETAILS.UNBLOCK_CARD.FIRST_ACCESS.MODAL.WRONG_CURRENT_PASSWORD',
        'warning'
      );
      return;
    }

    if (passwordsKey.newPassword === passwordsKey.currentPassword) {
      openNotification(
        'PAGES.CARDS.MY_CARDS.DETAILS.UNBLOCK_CARD.FIRST_ACCESS.MODAL.SAME_AS_CURRENT_PASSWORD',
        'warning'
      );
      return;
    }

    setModalFirstAccess(false);

    if (!cardDetails?.id) return;

    dispatch(
      CardActions.unlockFirstAccessV3(
        cardDetails.id,
        passwordsKey.currentPassword,
        passwordsKey.newPassword,
        firstAccessPin,
        (err) => {
          if (err) {
            const { message, isPinError } = pinValidationResolver({
              err,
              fallbackKey: ERROR_FALLBACKS.UNBLOCK,
            });

            handleError(message, isPinError, err.message);
            return;
          }

          handleSuccess(
            'PAGES.CARDS.MY_CARDS.DETAILS.UNBLOCK_CARD.FIRST_ACCESS.UNBLOCK_SUCCESS'
          );

          setPasswordsKey({ pin: '', currentPassword: '', newPassword: '' });
          setMyPassword(null);
          setFirstAccessPin('');
        }
      )
    );
  };

  const handleCloseCardCodeModal = () => {
    setModalCardCode(false);
    setCardCode('');
    setMyPassword(null);
    setFirstAccessPin('');
  };

  const handleConfirmCodeCard = () => {
    setModalCardCode(false);
    dispatch(
      CardActions.validateOwnerShip(cardCode ?? '', (err) => {
        if (err) {
          setCardCode('');
          setMyPassword(null);
          setFirstAccessPin('');
          openNotification(
            'PAGES.CARDS.MY_CARDS.DETAILS.UNBLOCK_CARD.FIRST_ACCESS.INVALID_CARD_CODE',
            'error'
          );

          return;
        }

        setPasswordsKey({ pin: '', currentPassword: '', newPassword: '' });
        setModalFirstAccess(true);
      })
    );
  };

  const renderFloatingContact = () => (
    <Tooltip
      title={translate('PAGES.CARDS.MY_CARDS.DETAILS.HELPER.CONTACT_SUPPORT')}
      placement="left"
    >
      <button
        className="card-details__floating-contact"
        onClick={() => setIsCardContactModalVisible(true)}
      >
        <CustomerServiceOutlined />
      </button>
    </Tooltip>
  );

  const shouldDisableActions = () => {
    const isPinInvalid = (value: string) => value.length !== 4;

    const isChangePasswordInvalid =
      modalTypeActions.type === TypeModalEnum.CHANGE_PASSWORD &&
      isPinInvalid(password);

    const isNewPasswordInvalid =
      passwordsKey.newPassword.length === 4 &&
      !validateCardPassword(passwordsKey.newPassword).valid;

    const isFirstAccessInvalid =
      modalFirstAccess &&
      ([passwordsKey.currentPassword, passwordsKey.newPassword].some(
        isPinInvalid
      ) ||
        isNewPasswordInvalid);

    return isChangePasswordInvalid || isFirstAccessInvalid;
  };

  const handleDigitCurrentPassword = (value: string) => {
    const onlyNumbers = value.replace(/\D/g, '');
    const limited = onlyNumbers.slice(0, 4);

    setPasswordsKey({ ...passwordsKey, currentPassword: limited });

    if (limited.length === 4 && limited !== myPassword) {
      openNotification(
        'PAGES.CARDS.MY_CARDS.DETAILS.UNBLOCK_CARD.FIRST_ACCESS.MODAL.WRONG_CURRENT_PASSWORD',
        'warning'
      );
    }
  };

  const handleDigitNewPassword = (value: string) => {
    const onlyNumbers = value.replace(/\D/g, '');
    const limited = onlyNumbers.slice(0, 4);

    setPasswordsKey({ ...passwordsKey, newPassword: limited });

    if (limited.length === 4) {
      const validation = validateCardPassword(limited);

      if (!validation.valid && validation.error) {
        openNotification(validation.error, 'warning');
        return;
      }

      if (limited === passwordsKey.currentPassword) {
        openNotification(
          'PAGES.CARDS.MY_CARDS.DETAILS.UNBLOCK_CARD.FIRST_ACCESS.MODAL.SAME_AS_CURRENT_PASSWORD',
          'warning'
        );
      }
    }
  };

  return (
    <>
      {renderFloatingContact()}
      <AdvancedHeader
        showBreadCrumbs
        breadCrumbs={[
          {
            active: true,
            title: translate('COMPONENTS.BREADCRUMB.HOME'),
            url: getRouteStackPath('HOME', 'HOME_PAGE'),
          },
          {
            active: false,
            title: translate('COMPONENTS.BREADCRUMB.CARDS'),
            url: getRouteStackPath('CARDS', 'CARDS_PAGE'),
          },
        ]}
      />
      <div className="card-details">
        <AdvancedPanelContentHeader
          testId="card-details-header-card"
          title={translate('PAGES.CARDS.MY_CARDS.DETAILS.TITLE')}
          description={translate('PAGES.CARDS.MY_CARDS.TEXT')}
          goBackAction={() =>
            history.push(getRouteStackPath('CARDS', 'MY_CARDS_PAGE'))
          }
        />
        <AdvancedCard testId="card-details-tabs-card">
          {isCardLoading ? (
            <div className="card-details__loading">
              <Spin size="large" />
            </div>
          ) : cardError ? (
            <div className="card-details__loading-error">
              <CloseCircleOutlined className="card-details__loading-error__icon" />
              <ThemedText variant="body">
                {translate(
                  'PAGES.CARDS.MY_CARDS.FUNCTIONALITY_UNAVAILABLE.TITLE'
                )}
              </ThemedText>
              <ThemedText variant="body">
                {translate(
                  'PAGES.CARDS.MY_CARDS.FUNCTIONALITY_UNAVAILABLE.SECOND_DESCRIPTION'
                )}
              </ThemedText>
            </div>
          ) : (
            <>
              {renderCardReceivedBanner()}
              {renderMessageCard()}
              <div className="card-details__card">{renderCardDetails()}</div>
              {renderBlockOrUnblock()}
              {renderCardActions()}
              {cardDetails?.type === CardType.PHYSICAL && renderCardControl()}
            </>
          )}
        </AdvancedCard>
        {cardDetails?.function === CardFunction.CREDIT && (
          <AdvancedCard testId="card-details-limits-card">
            <div className="card-details__section">
              <ThemedText variant="h3">
                {translate('PAGES.CARDS.MY_CARDS.DETAILS.LIMITS.TITLE')}
              </ThemedText>
            </div>
          </AdvancedCard>
        )}
      </div>
      <CardLimitUpdateModal
        visible={isUpdateLimitModalVisible}
        setVisible={setIsUpdateLimitModalVisible}
        onSubmit={handleChangeCardLimit}
        card={cardDetails || null}
      />
      <GenericModal
        visible={confirmAction !== null}
        onClose={() => setConfirmAction(null)}
        title={translate('PAGES.CARDS.MY_CARDS.DETAILS.CONFIRM_ACTION.TITLE')}
        description={translate(
          'PAGES.CARDS.MY_CARDS.DETAILS.CONFIRM_ACTION.DESCRIPTION'
        )}
        actions={[
          {
            label: translate('PAGES.CARDS.MY_CARDS.DETAILS.CANCEL'),
            onClick: () => setConfirmAction(null),
            variant: 'outlined',
          },
          {
            label: translate('PAGES.CARDS.MY_CARDS.DETAILS.CONFIRM'),
            onClick: handleConfirmAction,
            variant: 'primary',
          },
        ]}
      />
      <CardActionsModal
        modalTypeActions={modalTypeActions}
        onClose={() => handleCloseModal()}
        onConfirm={() =>
          unblockByIncorrectPassword()
            ? handleUnlockedByIncorrectPassword()
            : handlePinModal()
        }
        disabled={shouldDisableActions()}
        title={getModalTitleAndDescription().title}
        description={getModalTitleAndDescription().description}
        cancelDescription={description}
        onCancelDescriptionChange={(value) => setDescription(value)}
        cancelOptions={renderCancelOptions()}
        password={password}
        onPasswordChange={handleChangePassword}
        isFirstAccess={renderFirstAccess()}
        cardControlOptions={cardControlOptions}
        cardDetails={cardDetails}
      />
      <ConfirmationModal
        visible={modalPin}
        setVisible={setModalPin}
        handleConfirmation={handleSubmit}
      />
      <CardViewPasswordModal
        visible={showPassword}
        onClose={() => {
          setShowPassword(false);
          setShowViewPasswordValue(false);
          setMyPassword(null);
        }}
        myPassword={myPassword}
        showViewPasswordValue={showViewPasswordValue}
        onToggleVisibility={() => {
          const willShow = !showViewPasswordValue;
          setShowViewPasswordValue(willShow);
          if (willShow) {
            startViewPasswordVisibilityTimer();
          }
        }}
      />
      <CardFirstAccessModal
        visible={modalFirstAccess}
        onClose={handleCloseFirstAccess}
        onConfirm={handleSubmitFirstAccess}
        disabled={shouldDisableActions()}
        myPassword={myPassword}
        showCurrentPassword={showCurrentPassword}
        onToggleCurrentPasswordVisibility={() => {
          const willShow = !showCurrentPassword;
          setShowCurrentPassword(willShow);
          if (willShow) {
            startCurrentPasswordVisibilityTimer();
          }
        }}
        currentPassword={passwordsKey.currentPassword}
        onCurrentPasswordChange={handleDigitCurrentPassword}
        newPassword={passwordsKey.newPassword}
        onNewPasswordChange={handleDigitNewPassword}
      />
      <CardContact
        visible={isCardContactModalVisible}
        onClose={() => setIsCardContactModalVisible(false)}
      />
      <CardCodeModal
        visible={modalCardCode}
        onClose={handleCloseCardCodeModal}
        onConfirm={handleConfirmCodeCard}
        cardCode={cardCode}
        onCardCodeChange={(value) => setCardCode(value.replace(/\D/g, ''))}
      />
    </>
  );
};

export default Details;
